import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { ChevronRight, ChevronsLeft, ChevronsRight, Search, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/BrandMark";
import { cn } from "@/lib/utils";
import {
  NAV_GROUPS,
  PROJECT_DPR_PAGES,
  applyRoleFilter,
  filterGroups,
  flattenLeaves,
  findActivePath,
  isGroupActive,
} from "@/components/layout/navConfig";

const STORAGE_KEY = "sidebar-open-nodes";

function loadOpenIds() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function saveOpenIds(set) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    // sessionStorage unavailable — open state just won't persist this session
  }
}

export function Sidebar({ collapsed, onToggleCollapse }) {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isOrgAdmin, seesWholePortfolio } = useAuth();
  const [query, setQuery] = useState("");
  const [openIds, setOpenIds] = useState(loadOpenIds);

  const visibleGroups = useMemo(
    () => applyRoleFilter(NAV_GROUPS, { seesWholePortfolio, isOrgAdmin }),
    [seesWholePortfolio, isOrgAdmin]
  );

  useEffect(() => {
    const chain = findActivePath(visibleGroups, location.pathname, projectId);
    if (chain.length === 0) return;
    setOpenIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      chain.forEach((id) => {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      });
      if (changed) saveOpenIds(next);
      return changed ? next : prev;
    });
  }, [location.pathname, projectId, visibleGroups]);

  const toggleOpen = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveOpenIds(next);
      return next;
    });
  };

  const q = query.trim().toLowerCase();
  const filteredGroups = useMemo(() => filterGroups(visibleGroups, q, projectId), [visibleGroups, q, projectId]);
  const leafMatches = useMemo(() => flattenLeaves(filteredGroups, projectId), [filteredGroups, projectId]);
  const hasResults = leafMatches.length > 0;

  const clearSearch = () => setQuery("");

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      if (leafMatches[0]) {
        navigate(leafMatches[0].to);
        clearSearch();
      }
    } else if (e.key === "Escape") {
      clearSearch();
    }
  };

  return (
    <aside
      data-testid="app-sidebar"
      className={cn(
        "sticky top-0 flex h-screen shrink-0 flex-col border-r border-[#222226] bg-[#121215] transition-all duration-300 ease-in-out",
        collapsed ? "w-20" : "w-64"
      )}
    >
      <div className="flex h-16 shrink-0 items-center border-b border-[#222226] px-4">
        <BrandMark light collapsed={collapsed} tagline={!collapsed} />
      </div>

      {!collapsed && (
        <div className="shrink-0 px-3 pt-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="sidebar-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Jump to a module…"
              className="w-full rounded-lg border border-[#222226] bg-[#18181B] py-2 pl-8 pr-7 text-xs text-zinc-100 placeholder:text-zinc-500 focus:border-[#C8202E] focus:outline-none focus:ring-1 focus:ring-[#C8202E]"
            />
            {query && (
              <button
                data-testid="sidebar-search-clear-button"
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#F2575F]"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
        {collapsed ? (
          <div className="flex flex-col items-center gap-1.5">
            {visibleGroups.map((group) => (
              <CollapsedGroupIcon
                key={group.id}
                group={group}
                projectId={projectId}
                active={isGroupActive(group, location.pathname, projectId)}
              />
            ))}
          </div>
        ) : (
          <>
            {filteredGroups.map((group) => (
              <SidebarSection key={group.id} label={group.label} testId={`sidebar-group-${group.id}`}>
                <NavRows
                  items={group.items}
                  projectId={projectId}
                  openIds={openIds}
                  toggleOpen={toggleOpen}
                  onNavigate={clearSearch}
                  forceOpen={Boolean(q)}
                />
              </SidebarSection>
            ))}

            {q && !hasResults && (
              <p data-testid="sidebar-search-empty" className="px-3 text-xs text-zinc-500">
                No modules match "{query}"
              </p>
            )}
          </>
        )}
      </nav>

      <button
        data-testid="sidebar-collapse-toggle"
        onClick={onToggleCollapse}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className="flex shrink-0 items-center justify-center gap-2 border-t border-[#222226] py-3.5 text-zinc-400 transition-colors hover:bg-[#18181B] hover:text-[#F2575F]"
      >
        {collapsed ? (
          <ChevronsRight className="h-4 w-4" />
        ) : (
          <>
            <ChevronsLeft className="h-4 w-4" />
            <span className="text-xs font-medium">Collapse</span>
          </>
        )}
      </button>
    </aside>
  );
}

function SidebarSection({ label, testId, children }) {
  return (
    <div data-testid={testId}>
      <p className="mb-1.5 px-3 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">{label}</p>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

// Builds a flat row list via plain recursion (never a JSX element referencing
// itself — that trips the visual-edit tooling's portal-usage scan into an
// infinite loop) so rendering below is a single, non-recursive `.map()`.
function buildNavRows(items, depth, projectId, openIds, forceOpen, rows = []) {
  items.forEach((node) => {
    if (node.isProjectSection) {
      if (!projectId) return;
      const isOpen = forceOpen || openIds.has(node.id);
      rows.push({ kind: "toggle", id: node.id, slug: node.slug, label: node.label, icon: node.icon, depth, isOpen });
      if (isOpen) {
        const pages = node.dynamicChildren || PROJECT_DPR_PAGES;
        pages.forEach((p) => {
          rows.push({
            kind: "link",
            to: `/projects/${projectId}/${p.suffix}`,
            end: p.end,
            icon: p.icon,
            label: p.label,
            testId: p.testId,
            depth: depth + 1,
          });
        });
      }
      return;
    }
    if (node.children && node.children.length > 0) {
      const isOpen = forceOpen || openIds.has(node.id);
      rows.push({ kind: "toggle", id: node.id, slug: node.slug, label: node.label, icon: node.icon, depth, isOpen });
      if (isOpen) buildNavRows(node.children, depth + 1, projectId, openIds, forceOpen, rows);
      return;
    }
    rows.push({ kind: "link", to: node.to, end: node.end, icon: node.icon, label: node.label, testId: node.testId, depth });
  });
  return rows;
}

function NavRows({ items, projectId, openIds, toggleOpen, onNavigate, forceOpen }) {
  const rows = buildNavRows(items, 0, projectId, openIds, forceOpen);
  return rows.map((row, idx) =>
    row.kind === "toggle" ? (
      <SectionToggle
        key={row.id}
        label={row.label}
        icon={row.icon}
        depth={row.depth}
        isOpen={row.isOpen}
        onClick={() => toggleOpen(row.id)}
        testId={`sidebar-module-toggle-${row.slug}`}
      />
    ) : (
      <SidebarLink
        key={row.testId || `${row.to}-${idx}`}
        to={row.to}
        end={row.end}
        icon={row.icon}
        label={row.label}
        testId={row.testId}
        depth={row.depth}
        onClick={onNavigate}
      />
    )
  );
}

function SectionToggle({ label, icon: Icon, depth, isOpen, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      style={{ paddingLeft: 12 + depth * 14 }}
      className="flex w-full items-center gap-3 rounded-lg py-2.5 pr-3 text-sm font-medium text-zinc-300 transition-colors hover:bg-zinc-800/60 hover:text-white"
    >
      {Icon && <Icon className="h-[18px] w-[18px] shrink-0" />}
      <span className="flex-1 truncate text-left">{label}</span>
      <ChevronRight className={cn("h-3.5 w-3.5 shrink-0 text-zinc-500 transition-transform", isOpen && "rotate-90")} />
    </button>
  );
}

function SidebarLink({ to, end, icon: Icon, label, testId, depth = 0, onClick }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      data-testid={testId}
      style={{ paddingLeft: 12 + depth * 14 }}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-lg py-2.5 pr-3 text-sm font-medium transition-colors",
          isActive ? "bg-[#C8202E] text-white shadow-sm" : "text-zinc-300 hover:bg-zinc-800/60 hover:text-white"
        )
      }
    >
      {Icon && <Icon className="h-[18px] w-[18px] shrink-0" />}
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

// --- Collapsed mode: one icon per group, sub-pages surface in a hover flyout ---

function CollapsedGroupIcon({ group, projectId, active }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (open && anchor) {
      const rect = anchor.getBoundingClientRect();
      setCoords({ top: rect.top, left: rect.right + 8 });
    }
  }, [open, anchor]);

  const Icon = group.icon;

  return (
    <div
      ref={setAnchor}
      className="relative w-full"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <div
        data-testid={`sidebar-group-icon-${group.id}`}
        title={group.label}
        className={cn(
          "mx-auto flex h-10 w-10 items-center justify-center rounded-lg transition-colors",
          active ? "bg-[#C8202E] text-white" : "text-zinc-300 hover:bg-zinc-800/60 hover:text-white"
        )}
      >
        {Icon && <Icon className="h-[18px] w-[18px]" />}
      </div>

      {open &&
        createPortal(
          <div
            data-testid={`sidebar-flyout-${group.id}`}
            style={{ position: "fixed", top: coords.top, left: coords.left }}
            className="z-50 max-h-[80vh] w-64 overflow-y-auto rounded-lg border border-[#222226] bg-[#18181B] p-2.5 shadow-2xl"
          >
            <p className="mb-1.5 px-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
              {group.label}
            </p>
            <FlyoutRows items={group.items} projectId={projectId} onNavigate={() => setOpen(false)} />
          </div>,
          document.body
        )}
    </div>
  );
}

// Same flattening trick as NavRows — plain recursion into a flat list, never a
// JSX element referencing itself.
function buildFlyoutRows(items, projectId, rows = []) {
  items.forEach((node) => {
    if (node.isProjectSection) {
      if (!projectId) return;
      rows.push({ kind: "header", label: node.label, key: node.id });
      PROJECT_DPR_PAGES.forEach((p) => {
        rows.push({
          kind: "link",
          key: p.testId,
          to: `/projects/${projectId}/${p.suffix}`,
          end: p.end,
          icon: p.icon,
          label: p.label,
          testId: p.testId,
        });
      });
      return;
    }
    if (node.children) {
      rows.push({ kind: "header", label: node.label, key: node.id });
      buildFlyoutRows(node.children, projectId, rows);
      return;
    }
    rows.push({ kind: "link", key: node.id, to: node.to, end: node.end, icon: node.icon, label: node.label, testId: node.testId });
  });
  return rows;
}

function FlyoutRows({ items, projectId, onNavigate }) {
  return buildFlyoutRows(items, projectId).map((row) =>
    row.kind === "header" ? (
      <p key={row.key} className="mb-1 mt-2 px-2 text-[10px] font-bold uppercase tracking-wide text-zinc-500">
        {row.label}
      </p>
    ) : (
      <FlyoutLink key={row.key} to={row.to} end={row.end} icon={row.icon} label={row.label} testId={row.testId} onClick={onNavigate} />
    )
  );
}

function FlyoutLink({ to, end, icon: Icon, label, testId, onClick }) {
  return (
    <NavLink
      to={to}
      end={end ?? to === "/"}
      onClick={onClick}
      data-testid={testId}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors",
          isActive ? "bg-[#C8202E] text-white" : "text-zinc-300 hover:bg-zinc-700/60 hover:text-white"
        )
      }
    >
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
      <span className="truncate">{label}</span>
    </NavLink>
  );
}
